import React from 'react';
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { useLiveData } from '../hooks/useLiveData';
import { getAmbassadors, isConfigured } from '../lib/api';
import { ambassadors as mockAmb } from '../data/mockData';

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{background:'var(--bg-2)',border:'1px solid var(--border)',borderRadius:'6px',padding:'8px 12px'}}>
        <div style={{fontSize:'10px',color:'var(--text-3)',marginBottom:'4px'}}>{label}</div>
        <div style={{fontSize:'12px',color:'#ff6b35'}}>Referrals: {payload[0]?.value}</div>
      </div>
    );
  }
  return null;
};

export default function Ambassadors() {
  const configured = isConfigured();

  const { data: ambassadors, loading, lastUpdated } = useLiveData(
    configured ? getAmbassadors : () => Promise.resolve(mockAmb),
    60000
  );

  const list = ambassadors || [];
  const totalReferrals = list.reduce((s, a) => s + (a.referral_count || a.referrals || 0), 0);
  const premiums = list.filter(a => a.is_premium || a.status === 'premium').length;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Ambassador Boss</div>
          <div className="page-subtitle">
            MedSoc rep network · Auto-upgraded at 10 referrals
            {lastUpdated && <span style={{color:'var(--text-3)',marginLeft:'8px',fontSize:'10px'}}>· refreshed {Math.floor((new Date()-lastUpdated)/1000)}s ago</span>}
          </div>
        </div>
        <div style={{display:'flex',gap:'8px'}}>
          {!configured && <span style={{fontSize:'11px',color:'var(--text-3)',background:'var(--bg-2)',padding:'4px 10px',borderRadius:'20px',border:'1px solid var(--border)'}}>demo mode</span>}
          <button className="btn" style={{background:'var(--accent-mmf-dim)',color:'var(--accent-mmf)',border:'1px solid rgba(255,107,53,0.2)',fontSize:'12px',padding:'8px 16px'}}>+ Add Ambassador</button>
        </div>
      </div>

      <div className="grid-3" style={{marginBottom:'24px'}}>
        <div className="stat-card mmf">
          <div className="stat-label">Total Referrals</div>
          <div className="stat-value">{loading ? '—' : totalReferrals}</div>
          <div className="stat-sub">Across {list.length} reps</div>
        </div>
        <div className="stat-card gold">
          <div className="stat-label">Premium Reps</div>
          <div className="stat-value">{loading ? '—' : premiums}</div>
          <div className="stat-sub">Auto-upgraded by Ambassador Boss</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Attributed Revenue</div>
          <div className="stat-value">{loading ? '—' : `£${(totalReferrals * 20).toLocaleString()}`}</div>
          <div className="stat-sub">At £20/referral</div>
        </div>
      </div>

      {loading ? (
        <div style={{textAlign:'center',padding:'48px',color:'var(--text-3)'}}>Loading...</div>
      ) : (
        <div style={{display:'grid',gridTemplateColumns:'1fr 300px',gap:'20px',alignItems:'start'}}>
          <div className="card" style={{padding:'0',overflow:'hidden'}}>
            <table>
              <thead>
                <tr>
                  <th>Rep Handle</th>
                  <th>University</th>
                  <th>Referrals</th>
                  <th>Status</th>
                  <th>Progress to 10</th>
                </tr>
              </thead>
              <tbody>
                {list.map((amb, i) => {
                  const refs = amb.referral_count || amb.referrals || 0;
                  const isPrem = amb.is_premium || amb.status === 'premium';
                  return (
                    <tr key={i}>
                      <td style={{color:'var(--text)',fontWeight:'500'}}>{amb.handle}</td>
                      <td>{amb.university || amb.uni || '—'}</td>
                      <td style={{color:'var(--accent-mmf)',fontWeight:'600'}}>{refs}</td>
                      <td>
                        <span style={{fontSize:'9px',padding:'2px 7px',borderRadius:'4px',fontWeight:'600',letterSpacing:'0.06em',background:isPrem?'rgba(255,209,102,0.12)':'rgba(0,212,170,0.08)',color:isPrem?'var(--accent-gold)':'var(--accent-m)'}}>
                          {isPrem ? '★ PREMIUM' : 'ACTIVE'}
                        </span>
                      </td>
                      <td style={{width:'120px'}}>
                        <div style={{background:'var(--bg-3)',borderRadius:'10px',height:'4px',overflow:'hidden',marginBottom:'2px'}}>
                          <div style={{height:'100%',width:`${Math.min(100,(refs/10)*100)}%`,background:isPrem?'var(--accent-gold)':'var(--accent-m)',borderRadius:'10px'}}/>
                        </div>
                        <div style={{fontSize:'9px',color:'var(--text-3)'}}>{Math.min(refs,10)}/10</div>
                      </td>
                    </tr>
                  );
                })}
                {list.length === 0 && (
                  <tr><td colSpan="5" style={{textAlign:'center',padding:'32px',color:'var(--text-3)'}}>No ambassadors yet. Add your first MedSoc rep to get started.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div style={{display:'flex',flexDirection:'column',gap:'16px'}}>
            <div className="card">
              <div className="section-label">Referrals by Rep</div>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={list.map(a => ({ name: (a.handle||'').replace('@','').slice(0,10), referrals: a.referral_count||a.referrals||0 }))}>
                  <XAxis dataKey="name" tick={{fill:'var(--text-3)',fontSize:9}} axisLine={false} tickLine={false}/>
                  <Tooltip content={<CustomTooltip/>}/>
                  <Bar dataKey="referrals" fill="#ff6b35" radius={[3,3,0,0]} opacity={0.8}/>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="card">
              <div className="section-label">Auto-Upgrade Rules</div>
              {[
                {threshold:'10 referrals',reward:'Account → Premium',active:true},
                {threshold:'25 referrals',reward:'Exclusive merch drop',active:false},
                {threshold:'50 referrals',reward:'Revenue share 10%',active:false},
              ].map((rule,i) => (
                <div key={i} style={{display:'flex',gap:'10px',padding:'8px 0',borderBottom:'1px solid var(--border)'}}>
                  <div style={{width:'8px',height:'8px',borderRadius:'50%',marginTop:'4px',flexShrink:0,background:rule.active?'var(--green)':'var(--bg-3)',border:rule.active?'none':'1px solid var(--border-bright)'}}/>
                  <div>
                    <div style={{fontSize:'11px',color:'var(--text)',fontWeight:'500'}}>{rule.threshold}</div>
                    <div style={{fontSize:'10px',color:'var(--text-3)'}}>{rule.reward}</div>
                  </div>
                  {!rule.active && <span style={{marginLeft:'auto',fontSize:'9px',color:'var(--text-3)',alignSelf:'center'}}>Soon</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
